import { useEffect, useState } from 'react';
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { RiEyeLine, RiEyeOffLine, RiLoader4Line, RiLock2Line, RiUser3Line } from '@remixicon/react';
import { loginSchema, type LoginInput } from '@atnt/shared';
import { meQuery, useLogin } from '@/shared/api/auth';
import { ApiError } from '@/shared/api/client';
import { cn } from '@/shared/lib/cn';

export const Route = createFileRoute('/login')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

const YEAR = new Date().getFullYear();

/** Only same-origin paths are allowed as post-login targets. */
function safeRedirect(target: string | undefined) {
  if (!target) return '/';
  try {
    const url = new URL(target, window.location.origin);
    return url.origin === window.location.origin && !url.pathname.startsWith('/login') ? url.pathname + url.search : '/';
  } catch {
    return '/';
  }
}

function LoginPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const login = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema), defaultValues: { login: '', password: '' } });

  useEffect(() => setFocus('login'), [setFocus]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      await router.invalidate();
      await navigate({ href: safeRedirect(search.redirect), replace: true });
    } catch {
      /* error rendered below */
    }
  });

  const serverError = login.error instanceof ApiError ? login.error.message : login.error ? t('errors.generic') : null;

  return (
    <main className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-primary-bg-1000">
      {/* ambient background: grid + two accent glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'linear-gradient(#ffffff08 1px, transparent 1px), linear-gradient(90deg, #ffffff08 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
        }}
      />
      <div aria-hidden className="pointer-events-none absolute -top-160 -left-120 size-640 rounded-full bg-dark-blue-300 blur-[7.5rem]" />
      <div aria-hidden className="pointer-events-none absolute -right-160 -bottom-200 size-560 rounded-full bg-blue-200 blur-[7.5rem]" />

      <section className="relative z-10 w-440 rounded-16 border border-line bg-elevated p-32 shadow-float backdrop-blur-[var(--blur-glass)]">
        <header className="mb-28 flex items-center gap-12">
          <img src="/logo-uge.webp" alt="O'zbekgidroenergo" className="h-33 w-60 object-contain" />
          <span className="h-24 w-px bg-white-200" />
          <div className="min-w-0">
            <p className="text-15 leading-[1.2] font-bold text-fg">{t('app.name')}</p>
            <p className="text-11 leading-[1.4] text-fg-secondary">{t('auth.subtitle')}</p>
          </div>
        </header>

        <h1 className="mb-20 text-xl font-semibold text-fg">{t('auth.title')}</h1>

        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-16">
          <Field label={t('auth.login')} error={errors.login?.message}>
            <div className="relative">
              <RiUser3Line className="pointer-events-none absolute top-1/2 left-12 size-18 -translate-y-1/2 text-icon" />
              <input
                {...register('login')}
                autoComplete="username"
                placeholder={t('auth.loginPlaceholder')}
                className={inputClass(Boolean(errors.login))}
              />
            </div>
          </Field>

          <Field label={t('auth.password')} error={errors.password?.message}>
            <div className="relative">
              <RiLock2Line className="pointer-events-none absolute top-1/2 left-12 size-18 -translate-y-1/2 text-icon" />
              <input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder={t('auth.passwordPlaceholder')}
                className={cn(inputClass(Boolean(errors.password)), 'pr-44')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                className="absolute top-1/2 right-6 flex size-32 -translate-y-1/2 items-center justify-center rounded-6 text-icon hover:bg-hover hover:text-fg"
              >
                {showPassword ? <RiEyeOffLine className="size-18" /> : <RiEyeLine className="size-18" />}
              </button>
            </div>
          </Field>

          {serverError && (
            <p role="alert" className="rounded-8 border border-dark-red-300 bg-dark-red-100 px-12 py-10 text-13 text-fg">
              {serverError}
            </p>
          )}

          <button
            type="submit"
            disabled={login.isPending}
            className="mt-4 flex h-40 items-center justify-center gap-8 rounded-8 bg-accent text-sm font-semibold text-on-accent transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {login.isPending && <RiLoader4Line className="size-18 animate-spin" />}
            {t('auth.submit')}
          </button>
        </form>

        <footer className="mt-28 text-center text-11 text-fg-tertiary">© {YEAR} O'zbekgidroenergo · ATNT</footer>
      </section>
    </main>
  );
}

const inputClass = (invalid: boolean) =>
  cn(
    'h-40 w-full rounded-8 border bg-field pr-12 pl-38 t-field text-fg placeholder:text-fg-tertiary outline-none transition-colors',
    invalid ? 'border-dark-red-600' : 'border-transparent focus:border-focus',
  );

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-6">
      <span className="t-caps">{label}</span>
      {children}
      {error && <span className="text-11 text-danger">{error}</span>}
    </label>
  );
}
